import { type FC, useEffect } from "react";
import { BrowserRouter, Redirect, Route, Switch, useLocation } from "react-router-dom";

import "./App.scss";
import { AlertComponent, AlertProvider } from "@rhc-shared-components/alert-component";
import { RHCErrorPage } from "@rhc-shared-components/error-page";
import { checkAndSendAnalytics } from "@rhc-shared-components/rhc-eddl";
import { RHCAuthProvider, useRHCAuthContext } from "@rhc-shared-components/rhc-sso";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Dashboard from "./components/Dashboard/Dashboard";
import Env from "./utils/env";
import { getUserInfoFromJWT } from "./utils/fetch";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 2,
			refetchOnWindowFocus: false,
			staleTime: 10 * 60 * 1000,
		},
	},
});

const AuthenticatedApp: FC = () => {
	const { isAuthenticated } = useRHCAuthContext();
	const { pathname } = useLocation();

	if (isAuthenticated && Env.getEnvName() === Env.EnvNames.PROD) {
		Sentry.setUser(getUserInfoFromJWT());
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname triggers analytics on route change
	useEffect(() => {
		if (isAuthenticated) {
			checkAndSendAnalytics({ spaName: "partner-portal" });
		}
	}, [isAuthenticated, pathname]);

	return (
		<>
			<AlertComponent />
			{isAuthenticated ? (
				<AlertProvider>
					<Switch>
						<Redirect from="/" to={`${Env.routePrefix}/landing`} exact />
						<Redirect from={`${Env.routePrefix}`} to={`${Env.routePrefix}/landing`} exact />
						<Route
							exact
							path={[`${Env.routePrefix}/landing`, `${Env.routePrefix}/not-a-partner`]}
							component={Dashboard}
						/>
						<Route render={() => <RHCErrorPage errorStatus={404} />} />
					</Switch>
				</AlertProvider>
			) : null}
		</>
	);
};

const App: FC = () => (
	<QueryClientProvider client={queryClient}>
		<RHCAuthProvider>
			<BrowserRouter>
				<div role="main" className="app">
					<AuthenticatedApp />
				</div>
			</BrowserRouter>
		</RHCAuthProvider>
	</QueryClientProvider>
);

export default App;
